var _jsxFileName = "/codebuild/output/src2357418120/src/bitbucket.org/newyuinc/adc-polaris-web/src/components/settings/AccountInformation/AccountInfoAdult/index.tsx";
import React, { memo, useCallback, useEffect } from 'react';
import { IconClose, InformationalScreen, LoadingModal } from '@adc/parallax-component-library';
import { LocalDate } from '@js-joda/core';
import { useFormik } from 'formik';
import parsePhoneNumberFromString from 'libphonenumber-js';
import * as Yup from 'yup';
import { BirthdayInput, Footer, FormContainer, Input, MainContainer, PhoneInput } from 'Components/utility';
import { SHOW_MODAL } from 'Reducers/modal/types';
import i18n from 'Utilities/i18n';
import log from 'Utilities/log';
import { store } from 'Utilities/store';
const DEFAULT_PHONE_NUMBER_CODE = '+1'; // Parallax defaults to 🇺🇸 US

const AccountInfoAdult = ({
  patient,
  app,
  firstNameLimit,
  lastNameLimit,
  minAge,
  loading,
  showPhoneInput,
  phoneErrorTimestamp,
  onSave,
  onBack,
  hasFormChanged,
  validateDOBAndDisplayError,
  validateEntriesLengthAndDisplayError,
  validateIsMinor,
  showMinorRestrictionMessage
}) => {
  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoFormMinor.formField.firstName.errors.required')).trim(),
    lastName: Yup.string().required(i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoFormMinor.formField.lastName.errors.required')).trim(),
    dateOfBirth: Yup.string().required(i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoFormMinor.formField.dob.errors.required')),
    phoneNumber: Yup.string().test('test-valid-phone', i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.phoneNumber.errors.validPhoneNumber'), function (value) {
      var _parsePhoneNumberFrom;
      const hasPhoneInput = showPhoneInput && patient.phoneNumber; // User has registered phone number
      const hasEnteredPhone = (value === null || value === void 0 ? void 0 : value.length) && value !== DEFAULT_PHONE_NUMBER_CODE; // User has not registered phone number but types a number

      if (!hasPhoneInput && !hasEnteredPhone) {
        return true; // not required
      }
      if (!value || value.length < 7) {
        return this.createError({
          message: i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.phoneNumber.errors.validPhoneNumber')
        });
      }
      return ((_parsePhoneNumberFrom = parsePhoneNumberFromString(value, {
        extract: false
      })) === null || _parsePhoneNumberFrom === void 0 ? void 0 : _parsePhoneNumberFrom.isValid()) ?? false;
    })
  });
  const onSubmitForm = useCallback(async values => {
    const isValid = validateEntriesLengthAndDisplayError([{
      value: values.firstName,
      msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.firstNameMaxLength',
      count: firstNameLimit
    }, {
      value: values.lastName,
      msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.lastNameMaxLength',
      count: lastNameLimit
    }]);
    if (!isValid) {
      return;
    }
    try {
      onSave(values);
    } catch (err) {
      log.error(err);
    }
  }, [firstNameLimit, lastNameLimit, onSave, validateEntriesLengthAndDisplayError]);
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    isValid,
    isSubmitting,
    errors,
    touched,
    values,
    initialValues
  } = useFormik({
    initialValues: patient,
    validationSchema: validationSchema,
    onSubmit: onSubmitForm,
    validateOnBlur: true
  });
  const handleOnBack = useCallback(() => {
    onBack(initialValues, values);
  }, [initialValues, values, onBack]);
  useEffect(() => {
    setFieldError('phoneNumber', i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.phoneNumber.errors.enterDifferentNumber'));
  }, [phoneErrorTimestamp, setFieldError]);
  useEffect(() => {
    var _values$phoneNumber;
    if (!patient.phoneNumber && (_values$phoneNumber = values.phoneNumber) !== null && _values$phoneNumber !== void 0 && _values$phoneNumber.length && values.phoneNumber === DEFAULT_PHONE_NUMBER_CODE) {
      setFieldValue('phoneNumber', '');
    }
  }, [patient.phoneNumber, setFieldValue, values.phoneNumber]);
  return /*#__PURE__*/React.createElement(MainContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 183,
      columnNumber: 5
    }
  }, loading && /*#__PURE__*/React.createElement(LoadingModal, {
    "aria-label": "Loading",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 184,
      columnNumber: 19
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen, {
    topNavigationProps: {
      title: i18n.t('AccountInformationPersonalInfo.title'),
      buttonStart: {
        element: /*#__PURE__*/React.createElement(IconClose, {
          testID: "AccountInformationPersonalInfo.go-back",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 189,
            columnNumber: 22
          }
        }),
        onPress: handleOnBack
      },
      testID: 'AccountInformationPersonalInfo.title'
    },
    testID: "AccountInformationPersonalInfo",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 185,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 196,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Content, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 197,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(FormContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 198,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.label'),
    placeholder: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.placeholder'),
    label: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.label'),
    testID: "firstName",
    errorMessage: errors.firstName,
    isInvalid: errors.firstName && touched.firstName ? true : false,
    value: values.firstName,
    onChangeText: handleChange('firstName'),
    onBlur: handleBlur('firstName'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 199,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.label'),
    placeholder: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.placeholder'),
    label: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.label'),
    testID: "lastName",
    inputMode: "text",
    errorMessage: errors.lastName,
    isInvalid: errors.lastName && touched.lastName ? true : false,
    value: values.lastName,
    onChangeText: handleChange('lastName'),
    onBlur: handleBlur('lastName'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 216,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(BirthdayInput, {
    error: errors.dateOfBirth && touched.dateOfBirth ? true : false,
    errorMessage: errors.dateOfBirth,
    testID: "dateOfBirth",
    initialDate: values.dateOfBirth ? LocalDate.parse(values.dateOfBirth) : undefined,
    onDateChange: date => {
      setFieldTouched('dateOfBirth');
      const dateString = date.toString();
      const isValidDate = validateDOBAndDisplayError(dateString);
      if (isValidDate) {
        const isMinor = validateIsMinor(dateString, minAge);
        if (isMinor) {
          if (app === 'llu') {
            showMinorRestrictionMessage(minAge);
            return;
          }
          store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
              details: i18n.t('AccountInformationPersonalInfo.modals.minorDOBError.description')
            }
          });
          return;
        }
        setFieldValue('dateOfBirth', dateString);
        setFieldTouched('dateOfBirth');
      }
    },
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 234,
      columnNumber: 15
    }
  }), showPhoneInput && /*#__PURE__*/React.createElement(PhoneInput, {
    "aria-label": i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.phoneNumber.label'),
    label: i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.phoneNumber.label'),
    errorMessage: errors.phoneNumber,
    isInvalid: errors.phoneNumber && touched.phoneNumber ? true : false,
    testID: "phoneNumber",
    value: values.phoneNumber,
    onChangeText: handleChange('phoneNumber'),
    onBlur: handleBlur('phoneNumber'),
    maxLength: 15,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 272,
      columnNumber: 17
    }
  }), /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.country.label'),
    label: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.country.label'),
    testID: "country",
    value: i18n.t(`Global.microcopy.countries.${values.country}`),
    disabled: true,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 288,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.emailAddress.label'),
    label: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.emailAddress.label'),
    testID: "email",
    inputMode: "text",
    value: values.email,
    disabled: true,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 299,
      columnNumber: 15
    }
  }))), /*#__PURE__*/React.createElement(InformationalScreen.Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 313,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Footer, {
    buttonLabel: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.links.saveButton.label'),
    onButtonPress: handleSubmit,
    buttonTestID: "AccountInformationPersonalInfo.saveButton",
    disabled: !values.firstName || !values.lastName || !values.dateOfBirth || !isValid || isSubmitting || !hasFormChanged(initialValues, values),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 314,
      columnNumber: 13
    }
  })))));
};
export default /*#__PURE__*/memo(AccountInfoAdult);